import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PremiumCard } from "../components/PremiumCard";
import { PremiumButton } from "../components/PremiumButton";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { fadeIn, slideUp, staggerContainer } from "../animations";
import * as Icons from "lucide-react";

export const C2BSmartphoneVerkaufen = () => {
  const [refVorteile, isVorteileVisible] = useScrollReveal({ once: true });
  const [refMarken, isMarkenVisible] = useScrollReveal({ once: true });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1f7db6] via-[#143e6e] to-[#1f7db6]" />
        
        <motion.div
          className="absolute top-20 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h1 variants={slideUp} className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
            Smartphone verkaufen
          </motion.h1>
          <motion.p variants={slideUp} className="text-xl sm:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            Schnell und einfach zum fairen Preis - verkaufen Sie Ihr gebrauchtes Smartphone
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt Smartphone anbieten
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Vorteile */}
      <section ref={refVorteile} className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            animate={isVorteileVisible ? "visible" : "hidden"}
            variants={slideUp}
            className="text-4xl sm:text-5xl font-bold text-[#143e6e] text-center mb-16"
          >
            Warum Ihr Smartphone bei uns verkaufen?
          </motion.h2>
          
          <motion.div
            initial="hidden"
            animate={isVorteileVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              { icon: Icons.Smartphone, title: "Sofortangebot", desc: "Preis in Echtzeit berechnen" },
              { icon: Icons.TrendingUp, title: "Best Preise", desc: "Marktführende Ankaufspreise" },
              { icon: Icons.Lock, title: "Datenschutz", desc: "Professionelle Datenlöschung" },
              { icon: Icons.CheckCircle, title: "Einfach", desc: "Schneller Verkaufsprozess" }
            ].map((item, idx) => (
              <motion.div key={idx} variants={slideUp}>
                <PremiumCard className="p-8 text-center h-full">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#1f7db6] to-[#143e6e] rounded-2xl flex items-center justify-center"
                  >
                    <item.icon className="text-white" size={32} />
                  </motion.div>
                  <h3 className="font-bold text-xl mb-3 text-[#143e6e]">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Marken */}
      <section ref={refMarken} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            animate={isMarkenVisible ? "visible" : "hidden"}
            variants={slideUp}
            className="text-4xl sm:text-5xl font-bold text-[#143e6e] text-center mb-16"
          >
            Diese Smartphone-Marken kaufen wir an
          </motion.h2>

          <motion.div
            initial="hidden"
            animate={isMarkenVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto"
          >
            {['Apple iPhone', 'Samsung', 'Google Pixel', 'Huawei', 'OnePlus', 'Xiaomi', 'Sony', 'Oppo'].map((brand, idx) => (
              <motion.div key={idx} variants={slideUp}>
                <PremiumCard className="p-6 text-center">
                  <p className="font-semibold text-lg text-gray-800">{brand}</p>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1f7db6] via-[#143e6e] to-[#1f7db6]" />
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h2 variants={slideUp} className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Bereit, Ihr Smartphone zu verkaufen?
          </motion.h2>
          <motion.p variants={slideUp} className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Erhalten Sie jetzt Ihr Sofortangebot
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt Angebot erhalten
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};
